import { Pagination } from '../../components/ui/Pagination';
import { ErrorCard, LoadingCard } from '../../components/ui/ScreenFallback';
import { useAudit } from '../../hooks/useAudit';
import { auditPageLabel } from '../../lib/audit';
import { AuditFilters } from './AuditFilters';
import { AuditRow } from './AuditRow';

/**
 * Tela de Auditoria: toda ação manual do SICOP, com autor, alvo e horário.
 *
 * Registra o que *pessoas* fizeram. O que o cron faz sozinho continua na tela
 * de Serviços assíncronos — misturar as duas coisas encheria esta lista de
 * linhas sem autor, que é justamente o que ninguém vem procurar aqui.
 *
 * O controller nasce aqui, e não no App, pelo mesmo motivo das telas de Linhas
 * e Relatórios: a lista só interessa a esta tela.
 */
export function AuditScreen() {
  const controller = useAudit();
  const { page, users, userId, range, preset, filtered, loading, error } = controller;

  // Só a primeira carga bloqueia a tela; trocar de filtro ou de página mantém a
  // lista anterior no lugar, senão a tela pisca a cada ajuste do calendário.
  if (loading && !page && !error) {
    return <LoadingCard message="Carregando a auditoria…" />;
  }

  if (error && !page) {
    return (
      <ErrorCard
        title="Não foi possível carregar a auditoria."
        detail={error}
        onRetry={controller.reload}
      />
    );
  }

  return (
    <>
      <div className="text-ink-600 mb-3 text-[12.5px]">
        Toda ação manual fica registrada com autor, alvo e horário.
      </div>

      <AuditFilters
        users={users}
        userId={userId}
        range={range}
        preset={preset}
        filtered={filtered}
        onUserChange={controller.setUser}
        onRangeChange={controller.setRange}
        onPreset={controller.applyPreset}
        onClear={controller.clearFilters}
      />

      {error ? (
        <div className="border-danger-200 bg-danger-100 text-danger-600 mb-3 rounded-xl border px-4 py-3 text-[12.5px] font-semibold">
          {error}
        </div>
      ) : null}

      {page && page.entries.length > 0 ? (
        <div
          className={`border-line-200 overflow-hidden rounded-[14px] border bg-white ${
            loading ? 'opacity-60' : ''
          }`}
        >
          {page.entries.map((entry) => (
            <AuditRow key={entry.id} entry={entry} />
          ))}
        </div>
      ) : null}

      {page ? (
        <>
          {page.totalPages > 1 ? (
            <Pagination
              page={page.page}
              totalPages={page.totalPages}
              label={auditPageLabel(page.page, page.pageSize, page.total, filtered)}
              disabled={loading}
              onChange={controller.goToPage}
            />
          ) : (
            <div className="text-ink-400 mt-3 text-center text-[12.5px]">
              {auditPageLabel(page.page, page.pageSize, page.total, filtered)}
            </div>
          )}
        </>
      ) : null}
    </>
  );
}
